import { Check, X } from 'lucide-react'
import { HAS_UPPERCASE, HAS_SPECIAL } from '@dcp/shared'

/**
 * Compute a 0–4 entropy-style score from a password string.
 * Each met criterion below adds 1 point, capped at 4:
 *   - length >= 8
 *   - contains an uppercase letter
 *   - contains a special (non-alphanumeric) character
 *   - contains a digit
 *   - length >= 12
 *
 * Lowercase letters are intentionally NOT a criterion — users default to
 * lowercase anyway, so listing it as a requirement adds noise without
 * meaningful signal. The validation rule itself (uppercase + special) is
 * what the user must clear; see {@link meetsRequirements}.
 *
 * Returns 0 for an empty string.
 *
 * NOTE: This is a *strength* signal, not an acceptance signal. A password may
 * score "Fair" yet still fail the new-password schema (e.g. missing uppercase).
 * Use {@link meetsRequirements} for the binary accept/reject signal.
 */
export function computePasswordStrength(password: string): number {
  if (!password) return 0
  let score = 0
  if (password.length >= 8) score++
  if (HAS_UPPERCASE.test(password)) score++
  if (HAS_SPECIAL.test(password)) score++
  if (/\d/.test(password)) score++
  if (password.length >= 12) score++
  return Math.min(score, 4)
}

/**
 * Concrete validation requirements derived from `newPasswordSchema` in
 * `packages/shared/src/schemas/auth.ts`. Uses the same `HAS_UPPERCASE` /
 * `HAS_SPECIAL` regex parts that compose `PASSWORD_PATTERN`, so the UI
 * checklist cannot drift from what the schema actually enforces.
 */
export function meetsRequirements(password: string): {
  length: boolean
  uppercase: boolean
  special: boolean
} {
  return {
    length: password.length >= 8,
    uppercase: HAS_UPPERCASE.test(password),
    special: HAS_SPECIAL.test(password),
  }
}

/** True when every requirement in {@link meetsRequirements} passes. */
export function allRequirementsMet(password: string): boolean {
  const r = meetsRequirements(password)
  return r.length && r.uppercase && r.special
}

/**
 * Visual mapping for each strength level. Used only when the password meets
 * all hard requirements; otherwise bars render in the destructive palette so
 * the user sees an honest "not acceptable yet" signal.
 */
const LEVELS: ReadonlyArray<{ label: string; barClass: string; textClass: string }> = [
  { label: '', barClass: '', textClass: '' },
  { label: 'Weak', barClass: 'bg-destructive', textClass: 'text-destructive' },
  { label: 'Fair', barClass: 'bg-orange-500', textClass: 'text-orange-500' },
  { label: 'Good', barClass: 'bg-yellow-500', textClass: 'text-yellow-600' },
  { label: 'Strong', barClass: 'bg-green-500', textClass: 'text-green-600' },
]

/** Single line of the requirements checklist. */
function Requirement({ met, text }: { met: boolean; text: string }) {
  const Icon = met ? Check : X
  const color = met ? 'text-green-600' : 'text-muted-foreground'
  return (
    <li className={`flex items-center gap-1.5 text-xs ${color}`}>
      <Icon className="h-3 w-3" />
      <span>{text}</span>
    </li>
  )
}

/**
 * PasswordStrengthMeter
 *
 * Renders two coupled signals below a password input:
 *   1. Four entropy bars + a textual label (Weak/Fair/Good/Strong).
 *   2. A requirements checklist with green ✓ or muted ✗ for each rule the
 *      new-password schema enforces. The checklist is the authoritative
 *      accept/reject signal — submission still fails if any item is ✗.
 *
 * When any requirement is unmet, the bars render in the destructive color
 * regardless of entropy score, so the user is never misled by a "Fair" rating
 * on a password that the form will reject.
 */
export function PasswordStrengthMeter({ value }: { value: string }) {
  const score = computePasswordStrength(value)
  const reqs = meetsRequirements(value)
  const acceptable = allRequirementsMet(value)
  const { label, barClass, textClass } = LEVELS[score]

  // If the password is not yet acceptable, force destructive bars/text so
  // the entropy meter cannot suggest "Fair/Good" on an invalid password.
  const effectiveBarClass = acceptable ? barClass : 'bg-destructive'
  const effectiveTextClass = acceptable ? textClass : 'text-destructive'
  const effectiveLabel = acceptable ? label : 'Not yet acceptable'

  return (
    <div className="space-y-1.5" data-testid="password-strength-meter">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded ${i <= score ? effectiveBarClass : 'bg-muted'}`}
          />
        ))}
      </div>
      {effectiveLabel && (
        <p className={`text-xs ${effectiveTextClass}`}>
          {acceptable ? `Strength: ${effectiveLabel}` : effectiveLabel}
        </p>
      )}
      <ul className="space-y-0.5">
        <Requirement met={reqs.length} text="At least 8 characters" />
        <Requirement met={reqs.uppercase} text="At least 1 uppercase letter" />
        <Requirement met={reqs.special} text="At least 1 special character" />
      </ul>
    </div>
  )
}
